/**
 * Notification utilities for consistent message translation across all notification types
 */

/**
 * Build a localized notification message based on type and language
 * @param {Object} notification - The notification object
 * @param {string} senderName - The resolved sender name (or ID if not resolved)
 * @param {string} language - Current language ('ar' or 'en')
 * @returns {string} Localized message
 */
export function buildNotificationMessage(notification, senderName, language) {
  const isArabic = language === 'ar';
  // Prefer server-provided message when available (it may contain resolved names)
  const serverMsgRaw = (notification.originalMessage || notification.message || '').toString().trim();
  let serverMsg = serverMsgRaw;
  // detect JSON structured payload from backend
  let parsedServer = null;
  let serverIsJson = false;
  if (serverMsg && serverMsg.startsWith('{')) {
    try {
      parsedServer = JSON.parse(serverMsg);
      serverIsJson = true;
    } catch (e) {
      parsedServer = null;
      serverIsJson = false;
    }
  }
  // detect whether server message contains Arabic characters
  const serverContainsArabic = /[\u0600-\u06FF]/.test(serverMsg);
  // For non-Arabic UI, only prefer server message if it's clearly non-Arabic plain text
  if (serverMsg.length > 0 && !isArabic && !serverIsJson && !serverContainsArabic) {
    return serverMsg;
  }
  
  // Calendar Event
  if (notification.type === 'CALENDAR_EVENT' || notification.eventId) {
    const eventTitle = notification.eventTitle || notification.title || (isArabic ? "حدث جديد" : "New Event");
    const src = (notification.originalMessage || '').toLowerCase();
    const isDeclined = /declin|رفض/.test(src);
    const isAccepted = /accept|confirm|قبل|وافق/.test(src);
    if (isDeclined) {
      return isArabic
        ? `${senderName} رفض دعوتك إلى الحدث: ${eventTitle}`
        : `${senderName} declined your invitation to "${eventTitle}"`;
    }
    if (isAccepted) {
      return isArabic
        ? `${senderName} قبل دعوتك إلى الحدث: ${eventTitle}`
        : `${senderName} accepted your invitation to "${eventTitle}"`;
    }
    return isArabic
      ? `تمت دعوتك بواسطة ${senderName} إلى الحدث: ${eventTitle}`
      : `You have been invited by ${senderName} to the event: ${eventTitle}`;
  }
  
  // Leave notifications
  if (notification.type && notification.type.startsWith('LEAVE')) {
    // Use server-provided message when available for confirmations/status updates
    if (notification.type === 'LEAVE_CONFIRMATION' || notification.type === 'LEAVE_STATUS_UPDATE') {
      // If client language is English, prefer server-provided original message (may contain details)
      if (!isArabic && notification.originalMessage) return notification.originalMessage;

      // Detect explicit approve/reject keywords in server text
      const src = (notification.originalMessage || notification.eventTitle || notification.title || '').toString().toLowerCase();
      const serverText = (notification.originalMessage || notification.message || notification.eventTitle || notification.title || '').toString();
      const isReject = /reject|rejected|رفض|مرفوض|رفض الطلب/.test(src);
      const isApprove = /approve|approved|مواف|قبول/.test(src);

      // Try extract manager name from server English/Arabic text (e.g. "... has been rejected by Lujain ...")
      const managerFromServerEnMatch = serverText.match(/by\s+(.+?)(?:\.|$)/i);
      const managerFromServerArMatch = serverText.match(/بواسطة\s+(.+?)(?:\.|$)/);
      const managerFromServerEn = managerFromServerEnMatch ? managerFromServerEnMatch[1].trim() : null;
      const managerFromServerAr = managerFromServerArMatch ? managerFromServerArMatch[1].trim() : null;
      const managerNameDetected = managerFromServerEn || managerFromServerAr || null;

      const leaveType = notification.leaveType || '';
      const start = notification.startDate || notification.fromDate || notification.leaveStart || '';
      const end = notification.endDate || notification.toDate || notification.leaveEnd || '';
      const comments = notification.comments || notification.reason || notification.comment || notification.notes || '';

      if (isArabic) {
        if (isReject) {
          let msg = `تم رفض طلب الإجازة الخاصة بك`;
          if (leaveType) msg += ` (${leaveType})`;
          if (start || end) msg += ` من ${start || '...'} إلى ${end || '...'} `;
          const manager = managerFromServerEn || senderName;
          msg += `بواسطة ${manager}`;
          if (comments) msg += `. ملاحظات: ${comments}`;
          return msg;
        }
        if (isApprove) {
          let msg = `تمت الموافقة على طلب الإجازة الخاصة بك`;
          if (leaveType) msg += ` (${leaveType})`;
          if (start || end) msg += ` من ${start || '...'} إلى ${end || '...'} `;
          const manager = managerFromServerEn || senderName;
          msg += `بواسطة ${manager}`;
          if (comments) msg += `. ملاحظات: ${comments}`;
          return msg;
        }
      } else {
        if (isReject) {
          let msg = `Your ${leaveType || 'leave'} request`;
          if (start || end) msg += ` from ${start || '...'} to ${end || '...'} `;
          const manager = managerFromServerEn || senderName;
          msg += ` has been rejected by ${manager}`;
          if (comments) msg += `. Comments: ${comments}`;
          return msg;
        }
        if (isApprove) {
          let msg = `Your ${leaveType || 'leave'} request`;
          if (start || end) msg += ` from ${start || '...'} to ${end || '...'} `;
          const manager = managerFromServerEn || senderName;
          msg += ` has been approved by ${manager}`;
          if (comments) msg += `. Comments: ${comments}`;
          return msg;
        }
      }

      // Build a short localized confirmation/status message for Arabic or when server message is unavailable

      if (isArabic) {
        if (leaveType && start && end) return `${senderName} تم تحديث طلب الإجازة (${leaveType}) من ${start} إلى ${end}`;
        if (leaveType) return `تم تحديث طلب الإجازة (${leaveType})`;
        return `تم تحديث طلب الإجازة`;
      }

      // English fallback when server message not present
      if (notification.originalMessage) return notification.originalMessage;
      if (leaveType && start && end) return `${senderName} updated leave request (${leaveType}) from ${start} to ${end}`;
      if (leaveType) return `Your leave request (${leaveType}) has been updated`;
      return `Your leave request has been updated`;
    }

    // Admin-facing summary for approval requests
    if (notification.type === 'LEAVE_REQUEST') {
      const leaveType = notification.leaveType || '';
      if (leaveType) {
        return isArabic
          ? `${senderName} قدم طلب إجازة (${leaveType})`
          : `${senderName} submitted a leave request (${leaveType})`;
      }
      return isArabic
        ? `${senderName} قدم طلب إجازة جديدة`
        : `${senderName} submitted a new leave request`;
    }
  }

  // Fallback: if there's a leaveId but no explicit type, treat as admin summary
  if (notification.leaveId) {
    const leaveType = notification.leaveType || '';
    if (leaveType) {
      return isArabic
        ? `${senderName} قدم طلب إجازة (${leaveType})`
        : `${senderName} submitted a leave request (${leaveType})`;
    }
    return isArabic
      ? `${senderName} قدم طلب إجازة جديدة`
      : `${senderName} submitted a new leave request`;
  }
  
  // Document Request
  if (
    notification.type === 'DOCUMENT_REQUEST' ||
    notification.documentId ||
    notification.eventTitle === 'Document Request' ||
    /requested\s+a\s+/i.test(notification.originalMessage || '')
  ) {
    const serverText = String(notification.originalMessage || notification.message || notification.title || '');
    const statusSource = String(notification.originalMessage || notification.title || notification.message || '');
    const isRejected = /reject|rejected|رفض|مرفوض/i.test(statusSource);
    // Treat completion/finished as acceptance as servers may use 'completed' wording
    const isAccepted = /accept|accepted|approve|approved|completed|complete|finished|قبول|موافق|مكتمل|اكتمل|انتهى/i.test(statusSource);

    // Extract doc type: prefer explicit field, then parse from "requested a <docType>" pattern in server message
    const parsedDocTypeMatch = serverText.match(/requested\s+a\s+(.+?)(?:\s*\n|$)/i);
    const docType = notification.documentType
      || (parsedDocTypeMatch ? parsedDocTypeMatch[1].trim() : null)
      || (isArabic ? "مستند" : "document");

    // Extract requester name from serverText ("Name requested a docType") as fallback
    // when senderName is still an unresolved numeric ID
    const senderIsNumeric = /^\d+$/.test(String(senderName || '').trim());
    const parsedRequesterMatch = serverText.match(/^(.+?)\s+requested\s+a\s+/i);
    const nameFromMessage = parsedRequesterMatch ? parsedRequesterMatch[1].trim() : null;
    const displayName = senderIsNumeric
      ? (nameFromMessage || notification.fromName || senderName)
      : senderName;

    if (isRejected) {
      if (isArabic) {
        return docType ? `${docType}\nتم رفض طلب المستند الخاص بك` : `تم رفض طلب المستند الخاص بك`;
      }
      return `Your document request has been rejected.`;
    }

    if (isAccepted) {
      if (isArabic) {
        const docPart = docType ? ` (${docType})` : '';
        return `تم إكمال طلب المستند الخاص بك${docPart}`;
      }
      return `Your document request has been accepted.`;
    }

    // For new requests: show who requested and what doc type
    if (isArabic) {
      return `${displayName} طلب ${docType}`;
    }
    return `${displayName} requested a ${docType}`;
  }

  // Employee Action (salary change, promote, etc.) - provide localized text
  if (notification.type === 'EMPLOYEE_ACTION' || /تغيير راتب|salary change|salary|promote|promoted|transfer|terminate/i.test(serverMsg)) {
    // Try parse structured JSON message produced by backend
    let parsed = null;
    try {
      parsed = serverMsg && serverMsg.startsWith('{') ? JSON.parse(serverMsg) : null;
    } catch (e) {
      parsed = null;
    }

    // If backend provided structured names, prefer them
    if (parsed && (parsed.en || parsed.ar || parsed.actionType)) {
      const actionType = parsed.actionType || '';
      const nameEn = parsed.en || parsed.enName || parsed.nameEn || '';
      const nameAr = parsed.ar || parsed.arName || parsed.nameAr || '';

      // Always prefer the fully-localized titles the backend already built
      if (parsed.enTitle || parsed.arTitle) {
        const enTitle = parsed.enTitle || `${actionType} - ${nameEn}`;
        const arTitle = parsed.arTitle || `${actionType} - ${nameAr}`;
        return isArabic ? arTitle : enTitle;
      }

      // Fallback per action type if no pre-built title available
      const actionLabelsEn = {
        salary_change: 'Salary Change', rehire: 'Rehire', promote: 'Promote',
        demote: 'Demote', transfer: 'Transfer', terminate: 'Terminate',
        suspend: 'Suspend', contract: 'Contract', rotation: 'Rotation',
        review: 'Review', training: 'Training',
      };
      const actionLabelsAr = {
        salary_change: 'تغيير راتب', rehire: 'إعادة توظيف', promote: 'ترقية',
        demote: 'تخفيض رتبة', transfer: 'نقل', terminate: 'إنهاء خدمة',
        suspend: 'إيقاف', contract: 'عقد', rotation: 'دوران',
        review: 'مراجعة', training: 'تدريب',
      };
      const labelEn = actionLabelsEn[actionType] || actionType;
      const labelAr = actionLabelsAr[actionType] || actionType;

      if (isArabic) return `${labelAr} - ${nameAr || nameEn}`;
      return `${labelEn} - ${nameEn || nameAr}`;
    }

    // Fallback to previous heuristics when structured data isn't available
    const employeeName = (notification.employeeName || notification.employee || notification.title || '').toString();
    const isSalary = /تغيير راتب|راتب|salary/i.test(serverMsg) || /salary_change|salary-change|salaryChange/i.test(notification.actionType || '');
    const nums = (serverMsg || '').match(/\d{3,}(?:[\.,]\d+)?/g) || [];
    const firstNum = nums[0] || null;
    const secondNum = nums[1] || null;

    if (isArabic) {
      if (isSalary) {
        if (employeeName && firstNum && secondNum) return `تم تقديم طلب تغيير راتب لـ ${employeeName} من ${firstNum} إلى ${secondNum}`;
        if (employeeName && firstNum) return `تم تقديم طلب تغيير راتب لـ ${employeeName} إلى ${firstNum}`;
        if (employeeName) return `تم تقديم طلب تغيير راتب لـ ${employeeName}`;
        return `تم تقديم طلب تغيير راتب`;
      }
      if (employeeName) return `${employeeName} تم إنشاء طلب جديد`;
      return `تم إنشاء طلب جديد`;
    }

    if (isSalary) {
      if (employeeName && firstNum && secondNum) return `Salary change request for ${employeeName}: ${firstNum} → ${secondNum}`;
      if (employeeName && firstNum) return `Salary change request for ${employeeName}: ${firstNum}`;
      if (employeeName) return `Salary change request for ${employeeName}`;
      return `Salary change request`;
    }

    if (employeeName) return `New action request for ${employeeName}`;
    return `New action request`;
  }
  
  // Solfa/Advancement Request - summarized for managers and status updates
  if ((notification.type && notification.type.startsWith && notification.type.startsWith('ADVANCEMENT')) || notification.advancementId) {
    // Prefer structured fields if present
    const amount = notification.amount || notification.advancementAmount || notification.requestedAmount || notification.amountRequested;
    const installments = notification.installments || notification.installmentCount || notification.repaymentTerms;
    const src = (notification.originalMessage || notification.eventTitle || '').toString().toLowerCase();

    // Use fromName as fallback when senderName is still an unresolved numeric ID
    const advSenderIsNumeric = /^\d+$/.test(String(senderName || '').trim());
    const advDisplayName = advSenderIsNumeric
      ? (notification.fromName || senderName)
      : senderName;

    // Detect explicit status in server message/title and return localized status messages
    if (/reject|rejected|رفض|مرفوض|رفض الطلب/.test(src)) {
      if (isArabic) return `تم رفض طلب السلفة الخاصة بك بواسطة ${advDisplayName}`;
      return `${advDisplayName} rejected your advancement request`;
    }

    if (/approve|approved|مواف/.test(src)) {
      if (isArabic) return `تم الموافقة على طلب السلفة الخاصة بك بواسطة ${advDisplayName}`;
      return `${advDisplayName} approved your advancement request`;
    }

    if (/disburs|disbursed|صرف/.test(src)) {
      if (isArabic) return `تم صرف السلفة الخاصة بك بواسطة ${advDisplayName}`;
      return `${advDisplayName} disbursed the advancement`;
    }

    // Fallback: summary of the advancement request
    if (isArabic) {
      let base = `${advDisplayName} قدم طلب سلفة`;
      if (amount) base += ` (المبلغ: ${amount})`;
      if (installments) base += ` — الأقساط: ${installments}`;
      return base;
    }

    let base = `${advDisplayName} submitted an advancement request`;
    if (amount) base += ` (Amount: ${amount})`;
    if (installments) base += ` — Installments: ${installments}`;
    return base;
  }
  
  // Employee Change
  if (notification.type === 'EMPLOYEE_CHANGE' || notification.changeId) {
    return isArabic
      ? `${senderName} طلب تغيير في بيانات الموظف`
      : `${senderName} requested an employee data change`;
  }
  
  // Message/Chat
  if (notification.type === 'MESSAGE' || notification.messageId) {
    return isArabic
      ? `رسالة جديدة من ${senderName}`
      : `New message from ${senderName}`;
  }
  
  // Generic/Info - use original message if available, otherwise generic
  if (serverMsg && typeof serverMsg === 'string' && serverMsg.length) {
    // If message contains the sender ID (numeric), replace it
    // redact emails and phone numbers for privacy
    const redactEmail = (s) => s.replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '');
    const redactPhone = (s) => s.replace(/(\+?\d[\d\s().-]{6,}\d)/g, '');
    const numericPattern = /\b\d{4,}\b/g;
    let msg = serverMsg;
    msg = redactEmail(msg);
    msg = redactPhone(msg);
    // replace long numeric IDs with sender name
    msg = msg.replace(numericPattern, senderName);
    return msg;
  }
  
  // Default fallback
  return isArabic
    ? `إشعار جديد من ${senderName}`
    : `New notification from ${senderName}`;
}

/**
 * Resolve sender names from employee API
 * @param {Array} senderIds - Array of numeric sender IDs to resolve
 * @param {Object} cache - Ref object with .current holding the cache
 * @param {string} baseUrl - Base URL for API
 * @param {Function} authFetch - Optional authenticated fetch function
 * @returns {Promise<void>}
 */
export async function resolveSenderNames(senderIds, cache, baseUrl, authFetch = fetch) {
  if (!senderIds || senderIds.length === 0) return;
  
  await Promise.all(
    senderIds.map(async (id) => {
      try {
        const response = await authFetch(`${baseUrl}/employees/select/${id}`);
        if (!response.ok) return;
        
        const data = await response.json();
        const emp = data.employee || data.data || data;
        
        const enName = emp?.fullName || emp?.englishName || emp?.employeeName || emp?.name || emp?.sr || "";
        const arName = emp?.arabicName || emp?.arabic_name || emp?.arabicFullName || emp?.nameArabic || "";
        
        cache.current[id] = {
          en: enName || arName || id,
          ar: arName || enName || id,
        };
      } catch (e) {
        console.error(`Failed to resolve sender ${id}:`, e);
      }
    })
  );
}

/**
 * Extract notification metadata for message building
 * @param {Object} notification - Raw notification from API
 * @returns {Object} Structured notification data
 */
export function extractNotificationData(notification) {
  return {
    id: notification.id,
    from: notification.from,
    // Prefer explicit server-provided `type` if present
    type: notification.type || (notification.eventId ? 'CALENDAR_EVENT'
      : notification.leaveId ? 'LEAVE_REQUEST'
      : notification.documentId ? 'DOCUMENT_REQUEST'
      : notification.advancementId ? 'ADVANCEMENT_REQUEST'
      : notification.changeId ? 'EMPLOYEE_CHANGE'
      : notification.messageId ? 'MESSAGE'
      : (notification.title === 'Document Request' || /requested\s+a\s+/i.test(notification.message || '')) ? 'DOCUMENT_REQUEST'
      : 'info'),
    eventId: notification.eventId,
    leaveId: notification.leaveId,
    documentId: notification.documentId,
    advancementId: notification.advancementId,
    changeId: notification.changeId,
    messageId: notification.messageId,
    eventTitle: notification.title || notification.eventTitle,
    leaveType: notification.leaveType,
    documentType: notification.documentType,
    originalMessage: notification.message,
    read: notification.read,
    createdAt: notification.createdAt,
  };
}

/*
 * Build a one-line advancement summary (English)
 */
export function formatAdvancementOneLine(notification, senderName = '') {
  const amount = notification.amount || notification.advancementAmount || notification.requestedAmount || notification.amountRequested || '';
  const installments = notification.installments || notification.installmentCount || notification.repaymentTerms || (notification.repayment && notification.repayment.count) || '';
  const installmentAmount = notification.installmentAmount || notification.repaymentAmount || '';
  const repaymentStart = notification.repaymentStart || notification.repaymentStartDate || notification.repaymentStartAt || '';
  const netSalary = notification.netSalary || notification.salaryNet || '';
  const reason = notification.reason || notification.request || notification.notes || '';
  const urgency = notification.urgency || notification.priority || 'normal';

  let perInstallment = installmentAmount;
  if (!perInstallment && amount && installments) {
    const a = parseFloat(amount);
    const n = parseInt(installments, 10);
    if (!Number.isNaN(a) && !Number.isNaN(n) && n > 0) {
      perInstallment = (a / n).toFixed(2);
    }
  }

  return `${senderName} requests a ${amount} advance to be repaid in ${installments} monthly installments of ${perInstallment} (start: ${repaymentStart}); net salary ${netSalary}; reason listed as "${reason}"; urgency ${urgency}.`;
}
